import { acceptHMRUpdate, defineStore } from 'pinia';
import values from 'lodash/values';
import filter from 'lodash/filter';
import { useAppsStore } from '@/stores/apps.store';
import { useStarsStore } from '@/stores/stars.store';
import { useHistoryStore } from '@/stores/history.store';
import {
    fetchTrackTypes,
    fetchTrackType,
    updateTrackType,
    createTrackType,
    deleteTrackType,
} from '@/stores/track-types.utils';
import { createFrozenMap } from '@/vuex/utils';
import { RECENTLY_SELECTED_HISTORY_TYPES } from '@/vuex/modules/user-settings/history/history.constants';
import { formatCrossAppDisplayName } from '@/stores/apps.utils.js';

interface TrackTypeIcon {
    type: string;
    [key: string]: unknown;
}

export interface TrackTypeRecord {
    id: string;
    name?: string;
    group?: unknown;
    appId?: string;
    appIds?: string[];
    kind?: string;
    dirty?: boolean;
    isCoreEvent?: boolean;
    icon?: TrackTypeIcon;
    eventPropertyConfigurations?: unknown;
    eventPropertyNameList?: unknown;
    [key: string]: unknown;
}

interface TrackTypesState {
    error: unknown;
    fetching: boolean;
    map: Record<string, TrackTypeRecord>;
}

interface TrackTypeWritePayload extends Partial<TrackTypeRecord> {
    shouldStartProcessing?: boolean;
}

interface ApiResponse<T> {
    data: T;
}

interface RecentTrackType extends TrackTypeRecord {
    appIcon: unknown;
    appDisplayName: string | undefined;
    isStarred: boolean;
    icon: TrackTypeIcon;
    lastUsed: number;
}

let loadAllPromise: Promise<ApiResponse<TrackTypeRecord[]>> | null = null;

export const useTrackTypesStore = defineStore('trackTypes', {
    state: (): TrackTypesState => ({
        error: {},
        fetching: false,
        map: {},
    }),
    getters: {
        list: (state) => values(state.map),
        trackTypeById: (state) => (id: string) => state.map[id],
        trackTypesByAppId (): (appId: string) => TrackTypeRecord[] {
            return (appId: string) => filter(this.list, (trackType) => trackType.appId === appId);
        },
        analyticsTrackTypesListWithStars (): TrackTypeRecord[] | null {
            const isStarred = useStarsStore().getStarByItemId;
            if (this.fetching) return null;

            return this.list.map((trackType) =>
                Object.freeze({
                    id: trackType.id,
                    name: trackType.name,
                    group: trackType.group,
                    appId: trackType.appId,
                    appIds: trackType.appIds,
                    appIcon: trackType.appId === undefined
                        ? undefined
                        : useAppsStore().getIconByAppId(trackType.appId),
                    kind: trackType.kind,
                    dirty: trackType.dirty,
                    isCoreEvent: trackType.isCoreEvent,
                    isStarred: !!isStarred(trackType.id),
                    icon: trackType.icon || { type: 'zap' },
                    eventPropertyConfigurations: trackType.eventPropertyConfigurations,
                    eventPropertyNameList: trackType.eventPropertyNameList,
                }),
            );
        },
        recentTrackTypesInReporting (state): RecentTrackType[] {
            const appsStore = useAppsStore();
            const isStarred = useStarsStore().getStarByItemId;

            return useHistoryStore()
                .itemHistory(RECENTLY_SELECTED_HISTORY_TYPES.TRACKTYPE_RECENTLY_SELECTED)
                .filter(({ id }) => state.map[id])
                .map(({ id, timestamp }) => {
                    const trackType = state.map[id];

                    return {
                        id: trackType.id,
                        name: trackType.name,
                        group: trackType.group,
                        appId: trackType.appId,
                        appIds: trackType.appIds,
                        appIcon: trackType.appId === undefined
                            ? undefined
                            : appsStore.getIconByAppId(trackType.appId),
                        appDisplayName: formatCrossAppDisplayName(trackType, appsStore.map),
                        kind: trackType.kind,
                        dirty: trackType.dirty,
                        isCoreEvent: trackType.isCoreEvent,
                        isStarred: !!isStarred(trackType.id),
                        icon: trackType.icon || { type: 'zap' },
                        eventPropertyConfigurations: trackType.eventPropertyConfigurations,
                        eventPropertyNameList: trackType.eventPropertyNameList,
                        lastUsed: timestamp,
                    };
                });
        },
    },
    actions: {
        async loadAll ({ reFetch = false }: { reFetch?: boolean } = {}) {
            if (loadAllPromise && reFetch !== true) {
                await loadAllPromise;

                return;
            }

            this.fetching = true;
            loadAllPromise = fetchTrackTypes() as Promise<ApiResponse<TrackTypeRecord[]>>;

            try {
                const { data: trackTypes } = await loadAllPromise;
                this.fetching = false;
                this.map = createFrozenMap(trackTypes) as Record<string, TrackTypeRecord>;
            } catch (error) {
                loadAllPromise = null;
                this.fetching = false;
                this.error = error;
                throw error;
            }
        },
        async loadOne ({ reFetch = false, id }: { reFetch?: boolean; id: string }) {
            if (!reFetch && this.trackTypeById(id)) return;

            this.fetching = true;

            return fetchTrackType(id)
                .then(({ data: trackType }: ApiResponse<TrackTypeRecord>) => {
                    this.fetching = false;
                    this.map[trackType.id] = Object.freeze(trackType);
                })
                .catch((error: unknown) => {
                    this.fetching = false;
                    this.error = error;
                    throw error;
                });
        },
        async create (payload: TrackTypeWritePayload = {}) {
            this.fetching = true;

            return createTrackType(payload)
                .then(({ data: trackType }: ApiResponse<TrackTypeRecord>) => {
                    this.fetching = false;
                    this.map[trackType.id] = Object.freeze(trackType);
                })
                .catch((error: unknown) => {
                    this.fetching = false;
                    this.error = error;
                    throw error;
                });
        },
        async update ({ shouldStartProcessing = false, ...payload }: TrackTypeWritePayload = {}) {
            this.fetching = true;

            return updateTrackType(payload, shouldStartProcessing)
                .then(({ data: trackType }: ApiResponse<TrackTypeRecord>) => {
                    this.fetching = false;
                    this.map[trackType.id] = Object.freeze(trackType);
                })
                .catch((error: unknown) => {
                    this.fetching = false;
                    this.error = error;
                    throw error;
                });
        },
        async remove ({ id }: { id: string }) {
            this.fetching = true;

            return deleteTrackType(id)
                .then(() => {
                    this.fetching = false;
                    delete this.map[id];
                })
                .catch((error: unknown) => {
                    this.fetching = false;
                    this.error = error;
                    throw error;
                });
        },
    },
});

export function __resetLoadAllPromiseForUnitTests () {
    loadAllPromise = null;
}

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useTrackTypesStore, module.hot));
}
